import { useMemo, useState } from "react";
import { useApp } from "@/contexts/AppContext";
import Topbar from "@/components/layout/Topbar";
import RiskBadge from "@/components/RiskBadge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Download, Search } from "lucide-react";
import { downloadCSV } from "@/lib/csv";
import { toast } from "sonner";

const uniqueSorted = (rows, key) =>
  Array.from(new Set(rows.map((r) => r[key]).filter(Boolean))).sort();

const fmt = (n) => (Number.isFinite(n) ? n.toLocaleString() : "0");

// Hoisted filter options — stable references across renders.
const CRITICALITY_OPTIONS = ["High", "Medium", "Low"];
const RISK_OPTIONS = ["High", "Medium", "Low"];

export default function ForecastEngine() {
  const { forecast, settings } = useApp();
  const rows = forecast.rows;

  const [site, setSite] = useState("all");
  const [category, setCategory] = useState("all");
  const [supplier, setSupplier] = useState("all");
  const [criticality, setCriticality] = useState("all");
  const [risk, setRisk] = useState("all");
  const [q, setQ] = useState("");

  const sites = uniqueSorted(rows, "Site");
  const categories = uniqueSorted(rows, "Category");
  const suppliers = uniqueSorted(rows, "SupplierName");

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (site !== "all" && r.Site !== site) return false;
      if (category !== "all" && r.Category !== category) return false;
      if (supplier !== "all" && r.SupplierName !== supplier) return false;
      if (criticality !== "all" && r.Criticality !== criticality) return false;
      if (risk !== "all" && r.StockoutRisk !== risk) return false;
      if (q) {
        const needle = q.toLowerCase();
        if (!String(r.SKU).toLowerCase().includes(needle) && !String(r.ItemName).toLowerCase().includes(needle)) return false;
      }
      return true;
    });
  }, [rows, site, category, supplier, criticality, risk, q]);

  const onExport = () => {
    const cols = [
      "Site", "SKU", "ItemName", "Category", "CurrentStock", "AverageMonthlyUsage",
      "LeadTimeDemand", "SafetyStock", "ForecastDemand", "ReorderPoint",
      "RecommendedOrderQty", "StockoutRisk", "OverstockQty", "OverstockValue",
      "ReorderValue", "SupplierName", "Criticality",
    ];
    downloadCSV("avyntis_forecast.csv", filtered, cols);
    toast.success(`Exported ${filtered.length} rows to CSV`);
  };

  const cur = settings.currency;

  return (
    <>
      <Topbar
        title="Forecast Engine"
        subtitle={`Per-SKU forecast demand, reorder point, and risk scoring · horizon ${settings.forecastHorizon}d`}
        actions={
          <Button data-testid="export-forecast-btn" variant="outline" size="sm" className="gap-2" onClick={onExport}>
            <Download className="h-4 w-4" /> Export CSV
          </Button>
        }
      />

      <div className="flex-1 px-8 py-7 space-y-5">
        <div className="bg-white border border-slate-200 rounded-lg p-4 grid grid-cols-2 md:grid-cols-6 gap-3">
          <div className="relative col-span-2 md:col-span-1">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
            <Input
              data-testid="filter-search"
              placeholder="Search SKU or item…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-8 h-9 text-sm"
            />
          </div>
          <FilterSelect testid="filter-site" label="Site" value={site} onChange={setSite} options={sites} />
          <FilterSelect testid="filter-category" label="Category" value={category} onChange={setCategory} options={categories} />
          <FilterSelect testid="filter-supplier" label="Supplier" value={supplier} onChange={setSupplier} options={suppliers} />
          <FilterSelect testid="filter-criticality" label="Criticality" value={criticality} onChange={setCriticality} options={CRITICALITY_OPTIONS} />
          <FilterSelect testid="filter-risk" label="Stockout Risk" value={risk} onChange={setRisk} options={RISK_OPTIONS} />
        </div>

        <div className="bg-white border border-slate-200 rounded-lg" data-testid="forecast-results-table">
          <div className="px-5 py-4 border-b border-slate-200 flex items-baseline justify-between">
            <h3 className="font-heading font-semibold text-slate-900">Forecast Results</h3>
            <span className="text-xs text-slate-500 font-mono">{filtered.length} / {rows.length} items</span>
          </div>
          <div className="overflow-x-auto max-h-[70vh]">
            <table className="data-table">
              <thead className="sticky top-0 z-10">
                <tr>
                  <th>Site</th>
                  <th>SKU</th>
                  <th>Item</th>
                  <th>Category</th>
                  <th className="num">Current</th>
                  <th className="num">Forecast Demand</th>
                  <th className="num">Reorder Point</th>
                  <th className="num">Recommended Qty</th>
                  <th>Stockout Risk</th>
                  <th className="num">Overstock Qty</th>
                  <th className="num">Reorder Value</th>
                  <th>Supplier</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.SKU + r.Site}>
                    <td className="text-slate-500">{r.Site}</td>
                    <td className="font-mono text-slate-900">{r.SKU}</td>
                    <td className="text-slate-700">{r.ItemName}</td>
                    <td className="text-slate-500">{r.Category}</td>
                    <td className="num">{fmt(r.CurrentStock)}</td>
                    <td className="num">{fmt(r.ForecastDemand)}</td>
                    <td className="num">{fmt(r.ReorderPoint)}</td>
                    <td className="num font-semibold">{fmt(r.RecommendedOrderQty)}</td>
                    <td><RiskBadge level={r.StockoutRisk} /></td>
                    <td className="num">{fmt(r.OverstockQty)}</td>
                    <td className="num">{cur}{fmt(r.ReorderValue)}</td>
                    <td className="text-slate-500">{r.SupplierName}</td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={12} className="text-center text-sm text-slate-500 py-10">No items match current filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="h-9 text-xs">
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All {label}</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
